// @file Bridge for Vue - React cross reactivity.
import { useCallback, useEffect, useState } from 'react'
import { isReadonly, isRef, watch, type Ref as VueRef } from 'vue'

type BridgedStateHook<T> = () => [T, (value: T) => void]

/**
 * Creates a reactive bridge between a Vue ref and a React state.
 * @param vueRef Vue ref to bridge.
 * @returns A React hook. This hook returns a React state that is kept in sync with the Vue ref,
 * along with a setter function to update the state from React if needed.
 * @example
 * ```vue
 * <script setup>
 * const isVisible = ref(false) // 1. Make changes to this Vue ref...
 * const bridgedIsVisible = createBridgeToReactState(isVisible)
 *
 * onMounted(() => {
 *   root.render(
 *     <ReactComponent
 *       useIsVisible={bridgedIsVisible}
 *     />
 *   )
 * })
 * </script>
 * ```
 * ```jsx
 * const ReactComponent = ({ useIsVisible }) => {
 *   const [isVisible, setIsVisible] = useIsVisible() // 2. ...and access it in React as a state. This state is reactive!
 *                                                    // 3. We can even set its value here and it will be reflected in Vue.
 *
 *   return <>...</>
 * }
 * ```
 */
function createBridgeToReactState<T>(vueRef: VueRef<T>): BridgedStateHook<T> {
  return () => {
    const [reactState, setReactState] = useState<T>(vueRef.value)

    useEffect(() => {
      // Normally, a Vue watcher is bound to the component instance that created it. When that component
      // unmounts, the watcher is automatically stopped. This can be dangerous when this bridge is used
      // and the Vue component unmounts, the bridge is destroyed and reactivity is lost. However, this
      // case WON'T happen because we create the watcher inside the returned React hook. This hook is
      // only called when the React component mounts, so in Vue's perspective the watcher is created
      // asynchronously. The only way it can be stopped is to call the `unwatch` function manually,
      // which we do in the `useEffect` cleanup below.
      //
      // Also this _dark magic_ works because Vue's reactivity runs everywhere, even inside React 😇
      const unwatch = watch(
        vueRef,
        (newValue) => {
          // Vue ref is a stable object. If we just set it as-is, React won't trigger memoized hooks
          // like `useMemo` or `useCallback`. We need to spread the object to create a new reference.
          const realNewValue = Array.isArray(newValue)
            ? [...newValue]
            : typeof newValue === 'object' && newValue !== null
            ? { ...newValue }
            : newValue
          setReactState(realNewValue as unknown as T)
        },
        { immediate: true, deep: true },
      )

      return () => unwatch()
    }, [])

    const setState = useCallback(
      (value: T) => {
        // Ignore read-only refs, including computed refs without a setter.
        if (isRef(vueRef) && isReadonly(vueRef)) return

        // The Vue ref is the source of truth, so this setter function updates it.
        // The change will be captured by the watcher above and synced to the React state.
        vueRef.value = value
      },
      [vueRef],
    )

    return [reactState, setState]
  }
}

export { createBridgeToReactState }
export type { BridgedStateHook }
